import React, { useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { PublicTransitLayerEntityName } from '@api/entities/replancity_PublicTransitLine';
import { useTypedSelector } from '@redux/Hooks/storeSelectors';
import { selectMapLayerIdByEntityName } from '@redux/selectors/selectors';
import EntityEditor from '../../../entities/EntityEditor';
import EntityManagerCleaner from '../../../entities/EntityManagerCleaner';
import { isErrorResponse, isUuidString } from '@utils/utils';
import { flewToPoint, highlightLayersPaint } from '@redux/map/map-reducer';
import { useDispatch } from 'react-redux';
import { getFeatureCenter } from '@utils/mapUtils';
import { useMapFeatureLoader } from '@hooks/entites/useMapFeatureLoader';

const StopEdit = () => {
    const stopEntityName = PublicTransitLayerEntityName.STOPS;
    const routesEntityName = PublicTransitLayerEntityName.ROUTES;
    const { stopId } = useParams<any>();
    const stopsLayerId = useTypedSelector(state =>
        selectMapLayerIdByEntityName(state, PublicTransitLayerEntityName.STOPS)
    );
    const { loadFeature } = useMapFeatureLoader({ entityName: stopEntityName });
    const dispatch = useDispatch();

    const highlightStop = useCallback(
        (featureStateId: string) => {
            dispatch(
                highlightLayersPaint({
                    layerId: stopsLayerId,
                    featureStateIds: [featureStateId],
                })
            );
        },
        [stopsLayerId]
    );

    useEffect(() => {
        return () => {
            dispatch(
                highlightLayersPaint({
                    layerId: stopsLayerId,
                })
            );
        };
    }, [stopsLayerId]);

    useEffect(() => {
        void (async function () {
            if (isUuidString(stopId)) {
                const { feature } = await loadFeature({
                    entityId: stopId!,
                    entityName: stopEntityName,
                    layerId: stopsLayerId,
                    findFeatureParent: false,
                });

                if (feature && !isErrorResponse(feature)) {
                    const [lng, lat] = getFeatureCenter(feature);
                    if (lng && lat) {
                        dispatch(flewToPoint({ lng, lat, zoom: 15 }));
                    }

                    highlightStop(feature.properties.featureStateId.toString());
                }
            }
        })();
    }, [stopEntityName, routesEntityName, stopId, stopsLayerId]);

    //TODO fix "fly to" and stop highlighting from useTransitStopLoader

    const getUrlOnSaveFn = useCallback(
        (entity: any) => {
            if (!isUuidString(stopId)) {
                return `../`;
            }
        },
        [stopId]
    );

    const onDeleteFn = useCallback(() => '../../', []);

    return (
        <EntityManagerCleaner entityName={stopEntityName}>
            <EntityEditor
                entityId={stopId!}
                entityName={stopEntityName}
                layerId={stopsLayerId}
                presetProperty={'publicTransitPreset'}
                getUrlOnSaveFn={getUrlOnSaveFn}
                getUrlOnDeleteFn={onDeleteFn}
                showDeleteBtn={isUuidString(stopId)}
            />
        </EntityManagerCleaner>
    );
};

export default StopEdit;
